'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import Link from 'next/link'

import logoIcon from '@images/navbar/logo_main-nav.svg'

import styles from '@app/[locale]/v1/v1.module.scss'

type HeaderV1Props = {
  locale: string
  basePath?: string
  useSmoothScroll?: boolean
}

export default function HeaderV1({ locale, basePath = `/${locale}/v1`, useSmoothScroll = true }: HeaderV1Props) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [activeSection, setActiveSection] = useState<string>('')

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      setIsScrolled(currentScrollY > 50)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    // Add class to body when mobile menu is open to push content down
    if (isMenuOpen) {
      document.body.classList.add('mobileMenuOpen')
    } else {
      document.body.classList.remove('mobileMenuOpen')
    }
    return () => {
      document.body.classList.remove('mobileMenuOpen')
    }
  }, [isMenuOpen])

  useEffect(() => {
    // Close mobile menu when screen is resized to desktop size (md breakpoint = 768px)
    const handleResize = () => {
      if (window.innerWidth >= 768 && isMenuOpen) {
        setIsMenuOpen(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isMenuOpen])

  useEffect(() => {
    // Track active section based on scroll position
    const sections = ['compare', 'contact']
    
    const handleScroll = () => {
      const scrollPosition = window.scrollY
      const windowHeight = window.innerHeight
      const headerHeight = 100 // Approximate header height
      const triggerPoint = scrollPosition + headerHeight + 50 // Point where section becomes active
      
      // Clear active state if at the top
      if (scrollPosition < 200) {
        setActiveSection('')
        return
      }
      
      let activeId = ''
      let maxVisibleHeight = 0
      
      // Check each section to see how much is visible
      sections.forEach((sectionId) => {
        const element = document.getElementById(sectionId)
        if (element) {
          const rect = element.getBoundingClientRect()
          const elementTop = rect.top
          const elementBottom = rect.bottom
          
          // Calculate how much of the section is visible
          const visibleTop = Math.max(elementTop, headerHeight)
          const visibleBottom = Math.min(elementBottom, windowHeight)
          const visibleHeight = Math.max(0, visibleBottom - visibleTop)
          
          // Section is considered active if:
          // 1. It's top has passed the trigger point (scrolled past header)
          // 2. It has a significant portion visible
          if (elementTop <= triggerPoint && visibleHeight > 100) {
            if (visibleHeight > maxVisibleHeight) {
              maxVisibleHeight = visibleHeight
              activeId = sectionId
            }
          }
        }
      })
      
      setActiveSection(activeId)
    }

    // Throttle scroll events for better performance
    let ticking = false
    const throttledHandleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll()
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', throttledHandleScroll, { passive: true })
    window.addEventListener('resize', handleScroll, { passive: true })
    handleScroll() // Check on mount

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [])

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    if (!useSmoothScroll) {
      return
    }
    e.preventDefault()
    setIsMenuOpen(false) // Close mobile menu if open
    const element = document.getElementById(targetId)
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      })
    }
  }

  const handleLogoClick = () => {
    setIsMenuOpen(false) // Close mobile menu if open
  }

  return (
    <header className={`${styles.header} ${isScrolled ? styles.headerScrolled : ''}`}>
      <div className={styles.headerInner}>
        <Link href={`/${locale}`} className={styles.logoContainer} onClick={handleLogoClick}>
          <Image
            src={logoIcon}
            alt="SonicJobs"
            width={131}
            height={33}
            className={styles.logoImage}
          />
        </Link>

        <nav className={styles.nav}>
          {useSmoothScroll ? (
            <a
              href="#compare"
              className={`${styles.navLink} ${activeSection === 'compare' ? styles.navLinkActive : ''}`}
              onClick={(e) => handleSmoothScroll(e, 'compare')}
            >
              Why SonicJobs
            </a>
          ) : (
            <Link href={`${basePath}#compare`} className={styles.navLink}>
              Why SonicJobs
            </Link>
          )}
          <Link href={`/${locale}/v1/jobs`} className={styles.navLink}>
            Browse Jobs
          </Link>
          <Link href={`/${locale}/investors`} className={styles.navLink}>
            Computer Use Agents
          </Link>
          {useSmoothScroll ? (
            <a
              href="#contact"
              className={`${styles.navLink} ${activeSection === 'contact' ? styles.navLinkActive : ''}`}
              onClick={(e) => handleSmoothScroll(e, 'contact')}
            >
              Contact
            </a>
          ) : (
            <Link href={`${basePath}#contact`} className={styles.navLink}>
              Contact
            </Link>
          )}
        </nav>

        <button 
          className={styles.buttonGetStarted}
          onClick={(e) => {
            if (useSmoothScroll) {
              e.preventDefault()
              handleSmoothScroll(e as any, 'contact')
            }
          }}
        >
          Get Started
        </button>

        <button
          className={styles.menuButton}
          type="button"
          aria-label="Toggle menu"
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen((open) => !open)}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M4 7H20M4 12H20M4 17H20"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className={`${styles.mobileNav} ${isMenuOpen ? styles.mobileNavOpen : ''}`}>
        <nav className={styles.mobileNavList}>
          {useSmoothScroll ? (
            <a
              href="#compare"
              className={`${styles.mobileNavLink} ${activeSection === 'compare' ? styles.mobileNavLinkActive : ''}`}
              onClick={(e) => handleSmoothScroll(e, 'compare')}
            >
              Why SonicJobs
            </a>
          ) : (
            <Link href={`${basePath}#compare`} className={styles.mobileNavLink}>
              Why SonicJobs
            </Link>
          )}
          <Link href={`/${locale}/v1/jobs`} className={styles.mobileNavLink}>
            Browse Jobs
          </Link>
          <Link href={`/${locale}/investors`} className={styles.mobileNavLink}>
            Computer Use Agents
          </Link>
          {useSmoothScroll ? (
            <a
              href="#contact"
              className={`${styles.mobileNavLink} ${activeSection === 'contact' ? styles.mobileNavLinkActive : ''}`}
              onClick={(e) => handleSmoothScroll(e, 'contact')}
            >
              Contact
            </a>
          ) : (
            <Link href={`${basePath}#contact`} className={styles.mobileNavLink}>
              Contact
            </Link>
          )}
          <button 
            className={styles.mobileButtonGetStarted}
            onClick={(e) => {
              if (useSmoothScroll) {
                e.preventDefault()
                setIsMenuOpen(false)
                handleSmoothScroll(e as any, 'contact')
              }
            }}
          >
            Get Started
          </button>
        </nav>
      </div>
    </header>
  )
}
